import { useMemo } from 'react';
import { PlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import {
    actionsColumn,
    DataTable,
    expandAction,
    type DataTableAction,
    type DataTableColumn,
    type DataTableExpansion,
    type DataTableMode,
    type DataTablePagination,
    type DataTableSearchConfig
} from '@odo/shared-ui';
import { LastActivity } from '../../users/LastActivity';
import { RoleBadge } from '../../users/RoleBadge';
import { UserCell } from '../../users/UserCell';
import type { PracticeUserRow } from '../../../../services/practiceUsers';

const SKELETON_ROW_COUNT = 4;
const ROW_AVATAR_SIZE = 32;
const TD_PADDING = 'px-3 first:pl-5 last:pr-4';
const TH_PADDING = 'px-3 first:pl-5 last:pr-4';

function SkeletonCell({ width, circle }: { width: string; circle?: boolean }) {
    return (
        <div
            className={`bg-gray-200 animate-pulse ${circle ? 'w-8 h-8 rounded-full' : `h-3.5 rounded ${width}`}`}
        />
    );
}

function UserSkeletonCell() {
    return (
        <div className="flex items-center gap-3">
            <SkeletonCell width="" circle />
            <SkeletonCell width="w-32" />
        </div>
    );
}

function SkeletonRows() {
    const widths = ['w-16', 'w-20'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-16 border-b border-gray-200">
                    <td className={`${TD_PADDING} align-middle`}>
                        <UserSkeletonCell />
                    </td>
                    {widths.map((width, j) => (
                        <td key={j} className={`${TD_PADDING} align-middle`}>
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                    <td className={TD_PADDING} />
                </tr>
            ))}
        </>
    );
}

function UsersEmptyState({ onInvite }: { onInvite: () => void }) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm font-semibold text-gray-700">No users in this practice yet</p>
            <p className="text-[13px] text-gray-500 max-w-[380px]">
                Invite the front desk or the treating doctor and they&apos;ll get an email to set
                their password.
            </p>
            <button
                type="button"
                onClick={onInvite}
                className="flex items-center gap-2 bg-black text-white font-semibold px-4 h-9 rounded-lg text-sm hover:bg-gray-900 transition-colors mt-1"
            >
                <PlusIcon className="h-4 w-4" />
                Invite user
            </button>
        </div>
    );
}

// Same two-empty-states split as PatientsTable/CommunicationsTable: a practice can
// genuinely have zero users, or a search can simply have no matches.
function NoSearchResultsState({
    searchValue,
    onClear
}: {
    searchValue: string;
    onClear: () => void;
}) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">No users match &ldquo;{searchValue}&rdquo;.</p>
            <button
                type="button"
                onClick={onClear}
                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm mt-1"
            >
                Clear search
            </button>
        </div>
    );
}

interface PracticeUsersTableProps {
    users: PracticeUserRow[];
    isLoading: boolean;
    /**
     * Whether `users` came back from a non-empty committed server search - the
     * only signal needed to tell "no users at all" from "no search matches"
     * now that filtering happens server-side.
     */
    isSearching: boolean;
    search: DataTableSearchConfig;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    onInvite: () => void;
    onOpenUser: (user: PracticeUserRow) => void;
    onRemove: (user: PracticeUserRow) => void;
    expansion?: DataTableExpansion;
}

export function PracticeUsersTable({
    users,
    isLoading,
    isSearching,
    search,
    mode,
    onModeChange,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    onInvite,
    onOpenUser,
    onRemove,
    expansion
}: PracticeUsersTableProps) {
    const columns = useMemo<DataTableColumn<PracticeUserRow>[]>(
        () => [
            {
                key: 'user',
                label: 'User',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => (
                    <UserCell name={user.fullname} email={user.email} size={ROW_AVATAR_SIZE} />
                )
            },
            {
                key: 'role',
                label: 'Role',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => <RoleBadge isAdmin={user.isAdmin} />
            },
            {
                key: 'lastActivity',
                label: 'Last activity',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: `${TH_PADDING} whitespace-nowrap`,
                render: (user) => <LastActivity lastActivityAt={user.lastActivityAt} />
            },
            actionsColumn<PracticeUserRow>({
                key: 'actions',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (user) => (
                    <button
                        type="button"
                        onClick={() => onRemove(user)}
                        className="h-8 px-3 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                        Remove
                    </button>
                )
            })
        ],
        [onRemove]
    );

    const emptyState = useMemo(
        () =>
            isSearching ? (
                <NoSearchResultsState
                    searchValue={search.value}
                    onClear={() => search.onChange('')}
                />
            ) : (
                <UsersEmptyState onInvite={onInvite} />
            ),
        [isSearching, onInvite, search]
    );

    const actions = useMemo<DataTableAction[]>(
        () => [
            {
                key: 'invite',
                label: 'Invite user',
                icon: <PlusIcon className="h-4 w-4" />,
                variant: 'primary',
                onClick: onInvite
            },
            // After the main CTA on purpose - expand reads as secondary chrome.
            ...(expansion ? [expandAction(expansion, { label: 'Practice users' })] : [])
        ],
        [onInvite, expansion]
    );

    return (
        <DataTable
            label="Practice users"
            columns={columns}
            rows={users}
            getRowKey={(user) => user.id}
            rowAction={{ onActivate: onOpenUser }}
            tableLayout="auto"
            rowHeightClassName="h-16"
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={emptyState}
            emptyStateCellClassName="px-3 py-16"
            search={search}
            actions={actions}
            className="pb-8 overflow-hidden"
            expansion={expansion}
        />
    );
}
